import React from "react";
import moment from "moment";
import Swal from "sweetalert2";
import DOMPurify from "dompurify";
import {
  AUPATTERN,
  AU_BRANCH_CODE_LENGTH,
  COUNTRY_CODE_AU,
  COUNTRY_CODE_AU_SMALL,
  COUNTRY_CODE_NZ,
  COUNTRY_CODE_NZ_SMALL,
  CURRENCY_CLEANUP_REGEX,
  CURRENCY_FORMAT,
  DAY,
  DAYS_BACK,
  DAY_31,
  ENDPOINT_FORMAT_REGEX,
  MAX_CALL_COUNT,
  METHOD_FORMAT_REGEX,
  MMMYY,
  MONTH_11,
  NZPATTERN,
  PROD_ENV,
  PRIMARY_CONTACT_COUNTRY_PREFIXES,
  PRIMARY_CONTACT_NUMBER_REGEX,
  REMOVE_COMMA_REGEX,
  REPAYMENT_CUTOFF_HOUR,
  SYDNEY_TIMEZONE,
  STAGING_ENV,
  YYYYMMDD,
  ZERO
} from "./constants/appConstants";

import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import relativeTime from "dayjs/plugin/relativeTime";
import advancedFormat from "dayjs/plugin/advancedFormat";
import { MONTHS } from "./constants/dateFields";
import { FaCheck, FaMinusCircle, FaPlusCircle, FaTimes } from "react-icons/fa";
import { tooltips } from "../locales/en";
import { org_types } from "./data/data";

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(advancedFormat);
dayjs.extend(relativeTime);

const AUCKLAND_TIMEZONE = "Pacific/Auckland";

export const formatDateWithDayjs = (
  dateInput,
  format = "DD-MMM-YYYY HH:mm"
) => {
  if (!dateInput) return "";
  const output = dayjs.tz(dateInput, AUCKLAND_TIMEZONE).format(format);
  return output;
};

// Auckland Timezone Formatting
export const getFormattedAucklandTime = (format = "YYYY-MM-DD HH:mm:ss") => {
  return dayjs().tz(AUCKLAND_TIMEZONE).format(format);
};

// Current year and 0-based month index in the Auckland business zone. The
// monthly lending selector and buildMonthlyLendingSeries both key "today" to
// this zone, so the month a staff device offers and the month the series marks
// current agree regardless of the device timezone.
export const getAucklandYearMonth = () => {
  const now = dayjs().tz(AUCKLAND_TIMEZONE);
  return { year: now.year(), monthIndex: now.month() };
};

// function for dynamic time difference
export function getRelativeTime(dateInput) {
  if (!dateInput) return "";
  return dayjs.tz(dateInput, AUCKLAND_TIMEZONE).fromNow();
}

export const convertMonthFormat = (shortMonth) => {
  if (!shortMonth) return "";

  // Example input: "Feb-25"
  const parsed = dayjs(shortMonth, "MMM-YY", true);

  // Check if Day.js could parse it
  if (!parsed.isValid()) return "";

  // Output format: YYYY-MM
  return parsed.format("YYYY-MM");
};

export const getCurrentDate = () => {
  return dayjs().format("DD_MM_YY");
};

// utils/validation.js
export const isValidPhoneNumber = (phone) => {
  if (!phone) return false;
  return /^(\+?\d{1,3})? ?\d([\d ]{6,19})$/.test(phone.trim());
};

// Toast notification
export const showToast = (title, icon) => {
  const iconColors = {
    success: "#198754",
    error: "#dc3545",
    warning: "#ffc107",
    info: "#0dcaf0",
    question: "#0d6efd"
  };

  Swal.fire({
    toast: true,
    title: title,
    icon: icon,
    position: "bottom-end",
    color: iconColors[icon] || "",
    showConfirmButton: false,
    width: "320px",
    padding: "10px 5px",
    timer: 4000,
    timerProgressBar: true
  });
};

/**
 * Shown to a staff member who has just moved the repay date of an invoice the
 * customer's Xero already reports paid. A modal rather than a toast on purpose:
 * the advance is repayable now, so this is not something to let auto-dismiss.
 * Returns the Swal promise so a caller can wait for the acknowledgement.
 */
export const showSettledInXeroNotice = (message) =>
  Swal.fire({
    toast: false,
    icon: "warning",
    title: "Invoice already paid in Xero",
    text: message,
    confirmButtonText: "OK",
    confirmButtonColor: "#0d6efd"
  });

/**
 * The repay-date endpoints return this only when the invoice is already settled in
 * the customer's Xero and the caller was staff; a customer is refused outright.
 */
export const settledInXeroWarningOf = (response) =>
  response?.data?.data?.settledInXeroWarning;

// Allow Only Upto 4 Digits
export const formatToUSD = (value) => {
  if (value == null || isNaN(Number(value))) return "$0.00";
  return `$${Number(value).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  })}`;
};

// Convert Month String to Full Month Name || Example: "Jul-25" to "July"
const monthMap = {
  Jan: "January",
  Feb: "February",
  Mar: "March",
  Apr: "April",
  May: "May",
  Jun: "June",
  Jul: "July",
  Aug: "August",
  Sep: "September",
  Oct: "October",
  Nov: "November",
  Dec: "December"
};

export const formatMonth = (monthStr) => {
  const shortMonth = monthStr.split("-")[0];
  return monthMap[shortMonth] || monthStr;
};

// Alert notification
export const showConfirm = (title, text, icon) => {
  const iconColors = {
    success: "#A5DC86",
    error: "#F27474",
    warning: "#F8BB86",
    info: "#3FC3EE",
    question: "#87ADBD"
  };

  Swal.fire({
    title: title,
    text: text,
    icon: icon,
    position: "bottom-end",
    color: iconColors[icon] || "",
    showConfirmButton: false
  });
};

// Confirm notification
export const showAlert = (title, text, icon, confirm, cancel) => {
  const iconColors = {
    success: "#A5DC86",
    error: "#F27474",
    warning: "#F8BB86",
    info: "#3FC3EE",
    question: "#87ADBD"
  };

  return Swal.fire({
    title: title,
    text: text,
    icon: icon,
    iconColor: iconColors[icon] || "",
    showCancelButton: !!cancel,
    showConfirmButton: !!confirm,
    confirmButtonColor: confirm ? "#14bc96" : undefined,
    cancelButtonColor: cancel ? "#dc3545" : undefined,
    confirmButtonText: confirm,
    cancelButtonText: cancel
  });
};

// Confirm notification
export const showAlertWithHtml = (
  title,
  message,
  icon,
  confirm,
  cancel,
  addclass
) => {
  const iconColors = {
    success: "#A5DC86",
    error: "#F27474",
    warning: "#F8BB86",
    info: "#3FC3EE",
    question: "#87ADBD"
  };

  return Swal.fire({
    title: title,
    html: message,
    icon: icon,
    iconColor: iconColors[icon] || "",
    showCancelButton: true,
    confirmButtonColor: "#14bc96",
    cancelButtonColor: "#dc3545",
    confirmButtonText: confirm,
    cancelButtonText: cancel,
    customClass: {
      popup: addclass
    }
  });
};

// start from Current Month
export const generateMonthlyColumns = (startMonth, numberOfMonths) => {
  const columns = [];
  for (let i = 0; i < numberOfMonths; i++) {
    const month = moment(startMonth).subtract(i, "months");
    const monthHeader = month.format(MMMYY).toUpperCase();

    columns.push({
      Header: monthHeader,
      accessor: month.format("MMM_YY").toLowerCase(),
      columns: [
        {
          Header: "Completed",
          accessor: `completed_${i + 1}`
        }
      ]
    });
  }

  return columns;
};

export const generateMonths = () => {
  const monthsList = [];
  const now = new Date();
  const startDate = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  const endDate = new Date(now.getFullYear() - 3, now.getMonth(), 1);

  const currentDate = new Date(startDate);

  while (currentDate >= endDate) {
    const month = MONTHS[currentDate.getMonth()];
    const year = currentDate.getFullYear().toString().slice(-2);
    monthsList.push({
      month: `${month}-${year}`,
      value: `${month}-${year}`
    });
    currentDate.setMonth(currentDate.getMonth() - 1);
  }

  return monthsList;
};

// Names arrive in whichever Unicode form the keyboard, browser or clipboard produced, so "Tāne" typed
// one way is a different string from "Tāne" pasted another. NFC settles them into one form before the
// name is validated and stored, which keeps a stored name comparable with the same name typed again.
export const normalizeLegalName = (value = "") =>
  typeof value === "string" ? value.normalize("NFC") : value;

export const sanitizeInput = (value = "") => {
  return DOMPurify.sanitize(value, {
    ALLOWED_TAGS: ["br", "a"],
    ALLOWED_ATTR: ["href", "target", "rel", "style"]
  });
};

// The tags and attributes the rich text editor produces. `style` carries the
// editor's font size, colour and indent, and DOMPurify parses it rather than
// passing it through; every event-handler attribute and every tag outside this
// list is dropped, so markup pasted in from elsewhere cannot smuggle script in.
const RICH_TEXT_TAGS = [
  "p",
  "br",
  "strong",
  "b",
  "em",
  "i",
  "u",
  "s",
  "strike",
  "span",
  "ul",
  "ol",
  "li",
  "hr",
  "a",
  "blockquote",
  "h1",
  "h2",
  "h3",
  "h4",
  "h5",
  "h6"
];

// sanitizeInput allows only <br> and <a>, so it flattens formatted markup to
// plain text — right for the single-line fields that use it, and destructive
// for a rich text note. Rich text fields sanitise with this instead.
export const sanitizeRichText = (value = "") => {
  return DOMPurify.sanitize(value, {
    ALLOWED_TAGS: RICH_TEXT_TAGS,
    ALLOWED_ATTR: ["href", "target", "rel", "style"]
  });
};

export const formatComment = (comment) => {
  return comment.replace(/href="(www\.[\w-]+\.\w+)"/g, 'href="http://$1"');
};

// Helper for currency formatting
export const formatCurrency = (value) => {
  if (value === null || value === undefined || value === "") {
    return `0.00`;
  }

  const numberOnly = value?.toString().replace(/[^0-9.+-]/g, "");
  const parsed = parseFloat(numberOnly);
  if (isNaN(parsed)) {
    return `0.00`;
  }

  const absMantissa = Math.abs(parsed).toFixed(2);
  // Place minus sign before currency symbol when negative
  return (parsed < 0 ? "-" : "") + `${absMantissa}`;
};

export const formatCurrencyWithLocale = (value) =>
  `${Number(value).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  })}`;

export const renderCheckIcon = (value) =>
  value ? (
    <span style={{ color: "#14bc96" }}>
      <FaCheck />
    </span>
  ) : (
    <span className="secondary-color">
      <FaTimes />
    </span>
  );

export function formatNumber(value = "", locale = "en-US", fractionDigits = 2) {
  if (value === null || value === undefined || value === "") return "";

  const num = Number(value);
  if (isNaN(num)) return "";

  return num.toLocaleString(locale, {
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits
  });
}

export const convertToNumber = (number) => {
  return Number(number || 0).toFixed(2);
};

export const formatToLocaleString = (value, options = {}) => {
  if (value === null || value === undefined || isNaN(value)) return "0.00";

  const defaultOptions = {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
    ...options
  };

  return Number(value).toLocaleString(undefined, defaultOptions);
};
export function getRowDetailsIcon(isColumnbreack) {
  return isColumnbreack ? (
    <FaMinusCircle className="icon-status minus" />
  ) : (
    <FaPlusCircle className="icon-status plus" />
  );
}

export function getRowDetailsTooltip(isColumnbreack) {
  return isColumnbreack
    ? tooltips?.collapseRowDetails
    : tooltips?.expandRowDetails;
}

export const formatReferralOrgOptions = (data, allowedLabels) => {
  if (!data || typeof data !== "object") return [];

  return Object.keys(data)
    .filter((key) => allowedLabels.some((l) => l.value === key))
    .map((key) => ({
      label: key,
      options: data[key].map((item) => ({
        value: item.organisationName,
        label: item.organisationName
      }))
    }));
};

export const downloadFileFromResponse = (response) => {
  const contentDisposition = response.headers["content-disposition"];
  let filename = "downloaded_file";

  // Stateless logic: Extract filename using regex
  if (contentDisposition) {
    const filenameRegex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/;
    const matches = filenameRegex.exec(contentDisposition);
    if (matches != null && matches[1]) {
      filename = matches[1].replace(/['"]/g, "");
    }
  }

  // Stateless logic: Blob/DOM manipulation
  const blob = new Blob([response.data]);
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

export const generateColors = (numColors) => {
  const colors = [];
  for (let i = 0; i < numColors; i++) {
    const hue = (i * 360) / numColors;
    colors.push(`hsl(${hue}, 70%, 50%)`);
  }
  return colors;
};

export const roundToTwo = (num) => {
  if (num == null || isNaN(num)) return num;
  return Math.round((num + Number.EPSILON) * 100) / 100;
};

export const formatCurrencyData = (num) => {
  if (num == null || isNaN(num)) return "";

  return roundToTwo(num).toLocaleString(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2
  });
};

// Derive per-month series metrics from the monthly lending report payload.
// The backend sends a cumulative daily total per month; per-day amounts,
// the biggest single day and the month-to-date total are derived here so the
// chart and the summary read from one source.
export const buildMonthlyLendingSeries = (report) => {
  if (!report?.mainAmountArray) return [];

  // Pin "today" to the Auckland business zone, matching the backend report
  // boundary. A staff device behind NZ would otherwise report a day earlier and
  // truncate the current month's series one day short of the backend.
  const todayAuckland = dayjs().tz(AUCKLAND_TIMEZONE);
  const currentYearMonth = todayAuckland.format("YYYY-MM");
  const todayDate = todayAuckland.date();

  return report.mainAmountArray.map((arr, i) => {
    const key = report.months?.[i];
    const label = report.fullMonthNameArray?.[i];
    const color = `rgb(${report.colorCode?.[i]})`;
    // Colour class for the legend swatch/dot, keyed by calendar month (1-12) so
    // the class comes from the stylesheet rather than an inline style. Matches
    // the per-month palette in the backend report service.
    const monthNumber = key ? parseInt(key.split("-")[1], 10) : null;
    const colorClass = monthNumber
      ? `lending-color--${monthNumber}`
      : "lending-color--default";
    const isCurrentMonth = key === currentYearMonth;

    const cumulative = arr.map((value, idx) => {
      if (isCurrentMonth && idx + 1 > todayDate) return null;
      return value != null ? roundToTwo(value) : null;
    });

    let previous = 0;
    const perDay = cumulative.map((value) => {
      if (value == null) return null;
      const daily = roundToTwo(value - previous);
      previous = value;
      return daily;
    });

    let biggestDay = null;
    perDay.forEach((value, idx) => {
      if (value == null || value <= 0) return;
      if (!biggestDay || value > biggestDay.amount) {
        biggestDay = { index: idx, day: idx + 1, amount: value };
      }
    });

    let total = 0;
    let throughDay = 0;
    cumulative.forEach((value, idx) => {
      if (value != null) {
        total = value;
        throughDay = idx + 1;
      }
    });

    const activeDays = perDay.filter(
      (value) => value != null && value > 0
    ).length;
    const averagePerDay = activeDays ? roundToTwo(total / activeDays) : 0;

    return {
      key,
      label,
      color,
      colorClass,
      isCurrentMonth,
      cumulative,
      perDay,
      biggestDay,
      total,
      throughDay,
      averagePerDay
    };
  });
};

// Point styling for the Monthly Lending chart. The biggest funding day of each
// month is drawn larger with a black outline; every other day is the default.
const LENDING_POINT = {
  biggestRadius: 7,
  defaultRadius: 3,
  hoverRadiusBoost: 3,
  biggestBorderColor: "#000000",
  biggestBorderWidth: 2,
  defaultBorderWidth: 1
};

// Build the Chart.js data object (labels + one dataset per month) from the
// series that buildMonthlyLendingSeries produced. isPerDayView selects the
// per-day amounts over the cumulative running total.
export const buildMonthlyLendingChartData = (
  series,
  isPerDayView,
  dayLabels
) => {
  if (!series || series.length === 0) {
    return { labels: dayLabels, datasets: [] };
  }

  return {
    labels: dayLabels,
    datasets: series.map((month) => {
      const values = isPerDayView ? month.perDay : month.cumulative;
      const biggestIndex = month.biggestDay?.index;
      const isBiggest = (idx) => idx === biggestIndex;

      const pointRadius = values.map((_, idx) =>
        isBiggest(idx)
          ? LENDING_POINT.biggestRadius
          : LENDING_POINT.defaultRadius
      );

      return {
        label: month.label,
        data: values,
        borderColor: month.color,
        backgroundColor: month.color,
        pointBackgroundColor: month.color,
        pointRadius,
        pointHoverRadius: pointRadius.map(
          (radius) => radius + LENDING_POINT.hoverRadiusBoost
        ),
        pointBorderColor: values.map((_, idx) =>
          isBiggest(idx) ? LENDING_POINT.biggestBorderColor : month.color
        ),
        pointBorderWidth: values.map((_, idx) =>
          isBiggest(idx)
            ? LENDING_POINT.biggestBorderWidth
            : LENDING_POINT.defaultBorderWidth
        )
      };
    })
  };
};

export const getMonthlyLendingChartOptions = (
  dayLabels,
  userReports,
  isPerDayView = false,
  series = []
) => ({
  responsive: true,
  interaction: { mode: "nearest", intersect: true },
  stacked: false,

  plugins: {
    title: {
      display: true,
      text: isPerDayView
        ? userReports?.monthlyLendingReport?.titlePerDay
        : userReports?.monthlyLendingReport?.titleCumulative
    },
    tooltip: {
      mode: "nearest",
      intersect: true,
      callbacks: {
        label: (context) => {
          return `${context.dataset.label}: ${CURRENCY_FORMAT}${formatCurrencyData(context.raw)}`;
        },
        footer: (items) => {
          const item = items?.[0];
          const monthSeries = series?.[item?.datasetIndex];
          if (
            monthSeries?.biggestDay &&
            monthSeries.biggestDay.index === item?.dataIndex
          ) {
            return userReports?.monthlyLendingReport?.biggestDayTag;
          }
          return "";
        }
      }
    }
  },

  scales: {
    y: {
      type: "linear",
      display: true,
      position: "left",
      beginAtZero: true,
      title: {
        display: true,
        text: userReports?.monthlyLendingReport?.text2
      },
      ticks: {
        callback: (value) => `${CURRENCY_FORMAT}${formatCurrencyData(value)}`
      }
    },
    x: {
      labels: dayLabels,
      title: {
        display: true,
        text: userReports?.monthlyLendingReport?.text3
      }
    }
  },

  elements: {
    line: { borderWidth: 1 },
    point: {
      borderWidth: 1,
      radius: 3,
      hoverRadius: 6,
      hitRadius: 10
    }
  }
});

export const getForcedCountry = (isCountry) => {
  if (isCountry === COUNTRY_CODE_NZ) return COUNTRY_CODE_AU;
  if (isCountry === COUNTRY_CODE_AU) return COUNTRY_CODE_NZ;
  return isCountry;
};

export const normalizeInvoiceIds = (invoiceDetails) => {
  if (!Array.isArray(invoiceDetails)) return [];
  return invoiceDetails
    .flatMap((item) => String(item).split(","))
    .map((x) => Number(x.trim()))
    .filter((n) => !isNaN(n));
};

export const formatFTNumber = (value) => {
  if (value == null || value === "") return "";
  return `FT${value}`;
};

export const getFTNumber = (row) => {
  const id =
    Array.isArray(row?.childQuotes) && row?.childQuotes.length > 0
      ? row?.batchQuoteId
      : row?.id;
  return id ? `FT${id}` : "";
};

export const cleanQuoteId = (quoteId) => {
  if (!quoteId) return "";
  return quoteId.replace(/^FT/, "");
};

export const getSelectedFMFBIds = (selectedFlatRows) => {
  const ids = [];

  selectedFlatRows.forEach((row) => {
    const invoice = row.original;

    if (
      invoice.childQuotes &&
      invoice.childQuotes.length > 0 &&
      row.isExpanded
    ) {
      return;
    }

    if (invoice.childQuotes && invoice.childQuotes.length > 0) {
      ids.push(
        ...invoice.childQuotes
          .map((child) => child.batchQuoteId)
          .filter(Boolean)
      );
    }

    if (invoice.batchQuoteId) {
      ids.push(invoice.batchQuoteId);
    }
  });

  const uniqueIds = [...new Set(ids)];

  return {
    withFmFbFT: uniqueIds.map((id) => `FT${id}`).join(", "),
    withoutFmFbFT: uniqueIds.join(",")
  };
};

export const formatAmountField = (value) => {
  if (!value) return "";

  if (typeof value === "string" && /<span/i.test(value)) {
    const match = value.match(/>(.*?)</);
    const amountText = match ? match[1] : value;

    return {
      type: "html",
      value: amountText,
      color: "red"
    };
  }
  return {
    type: "text",
    value
  };
};

export const formatCurrencyWithSign = (value) => {
  const absValue = Math.abs(value).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });
  return value < 0 ? `-${absValue}` : `${absValue}`;
};
export const getTodayDate = () => new Date().toISOString().split("T")[0];

export const getUserCountryCode = () => localStorage.getItem("userCountryCode");

export const get14DaysAgoDate = () =>
  new Date(new Date().setDate(new Date().getDate() - DAYS_BACK));

export const normalizeOrgType = (apiValue = "") => {
  if (!apiValue) return "";

  const normalized = apiValue.toLowerCase().replace(/\s+/g, "");

  const matched = org_types.find(
    (item) => item.value.toLowerCase() === normalized
  );

  return matched?.value || "";
};

export const isValidNzPhone = (phone = "") => {
  const trimmedPhone = phone.trim();
  const NZ_PATTERN = NZPATTERN;
  return NZ_PATTERN.test(trimmedPhone);
};

export const isValidAuPhone = (phone = "") => {
  const trimmedPhone = phone.trim();
  const AU_PATTERN = AUPATTERN;
  return AU_PATTERN.test(trimmedPhone);
};

export const isValidPhoneLength = (numericValue = "") => {
  return numericValue.length >= 10 && numericValue.length <= 13;
};

export const isValidNameLength = (value) => {
  const trimmedLength = value?.trim().length || 0;
  return trimmedLength >= 2 && trimmedLength <= 50;
};

export const normalizePrimaryContactNumber = (value) => {
  let normalized = (value ?? "").trim().replace(/[\s\-().]/g, "");
  if (!normalized) return null;

  if (normalized.startsWith("00")) {
    normalized = `+${normalized.slice(2)}`;
  }

  if (!normalized.startsWith("+")) {
    if (normalized.startsWith("61") || normalized.startsWith("64")) {
      normalized = `+${normalized}`;
    } else if (normalized.startsWith("0") && normalized.length > 1) {
      // Infer region from the digit after the trunk 0: 2 -> NZ, 4 -> AU.
      const countryCode = normalized[1] === "4" ? "61" : "64";
      normalized = `+${countryCode}${normalized.slice(1)}`;
    } else {
      return null;
    }
  }

  // Users commonly type the trunk 0 even with a country code, e.g. "+64 021...".
  if (/^\+(61|64)0/.test(normalized)) {
    normalized = `+${normalized.slice(1, 3)}${normalized.slice(4)}`;
  }

  return /^\+\d+$/.test(normalized) ? normalized : null;
};

export const formatPrimaryContactNumber = (value) =>
  normalizePrimaryContactNumber(value) ?? value;

export const formatPrimaryContactNumberDisplay = (value) => {
  const clean = (value ?? "").replace(/[\s\-().]/g, "");
  if (!clean) return value;

  const countryPrefix = PRIMARY_CONTACT_COUNTRY_PREFIXES.find((candidate) =>
    clean.startsWith(candidate)
  );

  if (countryPrefix) {
    const rest = clean.slice(countryPrefix.length);
    if (!rest) return clean;
    const areaDigit = rest[0] === "0" ? rest[1] : rest[0];
    const isAu = areaDigit === "4";
    const groups = isAu
      ? [rest.slice(0, 3), rest.slice(3, 6), rest.slice(6)]
      : [rest.slice(0, 2), rest.slice(2, 5), rest.slice(5)];

    return `${countryPrefix} ${groups.filter(Boolean).join(" ")}`;
  }

  if (clean.startsWith("0") && clean.length > 1) {
    const isAu = clean[1] === "4";
    const groups = isAu
      ? [clean.slice(0, 4), clean.slice(4, 7), clean.slice(7)]
      : [clean.slice(0, 3), clean.slice(3, 6), clean.slice(6)];
    return groups.filter(Boolean).join(" ");
  }

  return value;
};

const PRIMARY_CONTACT_SIGNIFICANT_CHAR_REGEX = /[\d+]/;

export const getPrimaryContactNumberCursorPosition = (
  rawValue,
  rawCursor,
  formattedValue
) => {
  const countBefore = (rawValue ?? "")
    .slice(0, rawCursor)
    .split("")
    .filter((char) => PRIMARY_CONTACT_SIGNIFICANT_CHAR_REGEX.test(char)).length;

  if (countBefore === 0) return 0;

  let seen = 0;
  for (let i = 0; i < formattedValue.length; i += 1) {
    if (PRIMARY_CONTACT_SIGNIFICANT_CHAR_REGEX.test(formattedValue[i])) {
      seen += 1;
      if (seen === countBefore) return i + 1;
    }
  }

  return formattedValue.length;
};

export const isValidPrimaryContactNumber = (value) => {
  const normalized = normalizePrimaryContactNumber(value);
  return !!normalized && PRIMARY_CONTACT_NUMBER_REGEX.test(normalized);
};

export const isNumericPrimaryContactInput = (value) => {
  const clean = (value ?? "").trim().replace(/[\s\-().]/g, "");
  return /^\+?\d+$/.test(clean);
};

export const localDateString = (date) => {
  if (!date) return null;
  return dayjs.utc(date).local().format("YYYY-MM-DD");
};

export const getUserPrimaryCountry = () =>
  localStorage.getItem("userCountryCode") || "";

export const isValidCurrencyInput = (value = "") => {
  return /^[0-9,$]*$/.test(value);
};

// PPSR registration numbers differ by register: AU numbers are all digits
// (yyyymmdd + sequence), NZ numbers are alphanumeric. Both registers are
// entered through the same manual-upload field, and a comma separates multiple
// registrations, so the field accepts letters, digits and commas.
export const isValidPpsrRegistrationNumber = (value = "") => {
  return /^[A-Za-z0-9,]*$/.test(value);
};

// PIN / change numbers are digits on both registers; a comma separates
// multiple values.
export const isValidPpsrRegistrationPin = (value = "") => {
  return /^[\d,]*$/.test(value);
};

export const parseCurrencyToNumber = (value) => {
  if (value == null) {
    return 0;
  }
  if (typeof value === "number") {
    return value;
  }

  return parseInt(String(value).replace(/[$,]/g, ""), 10) || 0;
};

export const valueOrZero = (val) => {
  if (typeof val === "string") {
    return val.trim() ? val : ZERO;
  }
  return val != null ? val : ZERO;
};

export const formatCurrencyAmount = (value, currency = "", decimals = 2) => {
  const num = Number(value);
  return `${currency}${
    Number.isFinite(num)
      ? num.toLocaleString(undefined, {
          minimumFractionDigits: decimals,
          maximumFractionDigits: decimals
        })
      : "0.00"
  }`;
};

export const allowOnlyDigitsWithLimit = (value = "", limit = 4) => {
  return value.replace(/\D/g, "").slice(0, limit);
};
export const formatToDollar = (value) => {
  if (!value) return "";
  const number = parseInt(value.replace(/\D/g, ""), 10);
  if (isNaN(number)) return "";
  return `$${number.toLocaleString()}`;
};

export const formatTypeLabel = (type = "") =>
  type.replace(/[_-]+/g, " ").toUpperCase();

// Check if a date is a weekend (Saturday or Sunday) in NZT timezone
export const isWeekendNZT = (date) => {
  if (!date) return false;
  return [0, 6].includes(dayjs(date).tz(AUCKLAND_TIMEZONE).day());
};

// Check if a date is a weekend (Saturday or Sunday) in Sydney timezone
export const isWeekendAEST = (date) => {
  if (!date) return false;
  return [0, 6].includes(dayjs(date).tz(SYDNEY_TIMEZONE).day());
};

export const formatFileSize = (size) => {
  return (size / (1024 * 1024)).toFixed(2) + " MB";
};
export const getAucklandISOString = () => {
  return dayjs().tz(AUCKLAND_TIMEZONE).utc().format("YYYY-MM-DDTHH:mm:ss[Z]");
};

export const getMinRepaymentDate = () => {
  const nowSydney = dayjs().tz(SYDNEY_TIMEZONE);
  const todayIsWeekend = [0, 6].includes(nowSydney.day());
  const isBeforeCutoff = nowSydney.hour() < REPAYMENT_CUTOFF_HOUR;

  if (!todayIsWeekend && isBeforeCutoff) {
    return new Date();
  }

  const date = new Date();
  date.setDate(date.getDate() + 1);
  while (isWeekendNZT(date)) {
    date.setDate(date.getDate() + 1);
  }
  return date;
};

export const getRepaymentFloorDate = () => {
  const date = new Date();
  date.setDate(date.getDate() + 1);
  while (isWeekendNZT(date)) {
    date.setDate(date.getDate() + 1);
  }
  return date;
};

export const isRepaymentDateLocked = (repaymentDate, countryCode) => {
  if (!repaymentDate) return false;

  if (countryCode === COUNTRY_CODE_NZ) {
    const floor = getRepaymentFloorDate();
    floor.setHours(0, 0, 0, 0);
    const repay = new Date(repaymentDate);
    repay.setHours(0, 0, 0, 0);
    return repay < floor;
  }

  const nowSydney = dayjs().tz(SYDNEY_TIMEZONE);
  const cutoffMoment = dayjs(repaymentDate)
    .tz(SYDNEY_TIMEZONE)
    .startOf("day")
    .add(REPAYMENT_CUTOFF_HOUR, "hour");
  return !nowSydney.isBefore(cutoffMoment);
};

// Maps the backend-computed row.rowHighlightType directly to a CSS class - the UI does not
// replicate any of the backend's green/void/user-entered predicate evaluation itself.
const ROW_HIGHLIGHT_CLASS_MAP = {
  GREEN_HIGHLIGHT: "row-highlight-green",
  VOID_OR_DELETE: "row-highlight-void",
  USER_ENTERED: "row-highlight-user-entered"
};

export const getRowHighlightClass = (rowHighlightType) => {
  return ROW_HIGHLIGHT_CLASS_MAP[rowHighlightType] || "";
};

const OVERDUE_PERCENT_REGEX = /(\d+(\.\d+)?)%/;

// Parses the leading percentage out of aiOverduePrediction.overdue_output
// (e.g. "6.72%; Above Average Yes [Overdue Model New] | Limit: $300K" -> 6.72).
export const extractOverduePercent = (aiOverduePrediction) => {
  const overdueOutput = aiOverduePrediction?.overdue_output;
  if (!overdueOutput) return null;
  const match = OVERDUE_PERCENT_REGEX.exec(overdueOutput);
  return match ? parseFloat(match[1]) : null;
};

// A batch's own rowHighlightType only reflects its representative quote, not each child, so a
// child row is colored per its own fields:
// - parent null/DEFAULT -> every child is the same neutral gray.
// - parent USER_ENTERED -> a child whose own alerts also say "User Entered" gets a lighter tint
//   of the USER_ENTERED yellow; any other child falls back to the neutral gray.
// - parent VOID_OR_DELETE -> a child whose own alerts mention VOID/DELETE gets a lighter tint of
//   the VOID_OR_DELETE red; any other child falls back to the neutral gray.
// - parent GREEN_HIGHLIGHT -> a child whose own alert mentions VOID/VOIDED is flagged red;
//   otherwise, if it has any alert, is over 61 days, or its own AI overdue score is missing or
//   above 16 (stale/risky despite the batch qualifying as green overall), it gets the same
//   neutral gray as a null/DEFAULT batch rather than staying green.
// - anything else -> unchanged (existing childrows styling).
export const getChildQuoteHighlightClass = (child) => {
  const parentType = child?.rowHighlightType;
  const alerts = child?.alerts;
  const hasAlert = !!alerts;
  const upperAlerts = hasAlert ? alerts.toUpperCase() : "";

  if (!parentType || parentType === "DEFAULT") {
    return "childrows-default";
  }

  if (parentType === "USER_ENTERED") {
    return upperAlerts.includes("USER ENTERED")
      ? "childrows-user-entered-light"
      : "childrows-default";
  }

  if (parentType === "VOID_OR_DELETE") {
    return upperAlerts.includes("VOID") || upperAlerts.includes("DELETE")
      ? "childrows-void-light"
      : "childrows-default";
  }

  if (parentType !== "GREEN_HIGHLIGHT") {
    return "childrows";
  }

  if (hasAlert && upperAlerts.includes("VOID")) {
    return "childrows-void";
  }

  const overduePercent = extractOverduePercent(child?.aiOverduePrediction);
  const isOverdueRisky = overduePercent === null || overduePercent > 16;

  if (hasAlert || Number(child?.totalDays) > 61 || isOverdueRisky) {
    return "childrows-default";
  }

  return "childrows";
};

export const formatMethodValue = (value) => {
  return value.replace(METHOD_FORMAT_REGEX, "").toUpperCase();
};

export const formatEndpointValue = (value) => {
  return value.replace(ENDPOINT_FORMAT_REGEX, "").toLowerCase();
};

export const getMaxDateByMonthOffset = () => {
  const current = new Date();
  return new Date(current.getFullYear(), MONTH_11, DAY_31);
};

export const getSelectedNavItemNames = (checkedItems, adminMenuPermissions) => {
  return Object.keys(checkedItems)
    .filter((key) => checkedItems[key])
    .map((key) => {
      const parent = adminMenuPermissions.find(
        (item) => String(item.id) === key
      );

      if (parent) {
        return parent.slugName;
      }

      for (const item of adminMenuPermissions) {
        const child = item.children?.find(
          (childItem) => String(childItem.id) === key
        );

        if (child) {
          return child.slugName;
        }
      }

      return null;
    })
    .filter(Boolean);
};

export const capitalizeFirstLetter = (string) => {
  if (!string) return "";
  return String(string).charAt(0).toUpperCase() + String(string).slice(1);
};

export const get3MonthsAgo = () => {
  const d = new Date();
  d.setMonth(d.getMonth() - MAX_CALL_COUNT);
  return d;
};

export const getSelectedRowData = (selectedFlatRows, extractFn) => {
  const result = [];

  selectedFlatRows.forEach((row) => {
    const original = row.original;

    if (
      original.childQuotes &&
      original.childQuotes.length > 0 &&
      row.isExpanded
    ) {
      return;
    }

    if (original.childQuotes && original.childQuotes.length > 0) {
      result.push(...original.childQuotes.map((child) => extractFn(child)));
    } else {
      result.push(extractFn(original));
    }
  });

  return result.filter((val) => val !== undefined && val !== null);
};

export const parseCurrencyValue = (value) => {
  const cleaned = String(value || "")
    .replace(REMOVE_COMMA_REGEX, "")
    .replace(CURRENCY_CLEANUP_REGEX, "");

  const numberValue = Number(cleaned);

  return Number.isNaN(numberValue) ? 0 : numberValue;
};

export const countryMap = {
  61: COUNTRY_CODE_AU_SMALL,
  64: COUNTRY_CODE_NZ_SMALL
};

export const isRecaptchaEnabled = (environment) =>
  [PROD_ENV, STAGING_ENV].includes(environment);

export const parseAucklandDate = (dateString) => {
  if (!dateString) return null;
  return dayjs.tz(dateString, YYYYMMDD, AUCKLAND_TIMEZONE);
};

// Both ends are read in NZT so the day count can't pick up a fractional day
// from a device on another offset, which truncation would turn into ±1 day.
export const isOver90Days = (invoice) => {
  if (!invoice?.dueDate) return false;

  const dueDate = parseAucklandDate(invoice.dueDate)?.startOf(DAY);
  const termStart = invoice?.invoiceDate
    ? parseAucklandDate(invoice.invoiceDate)?.startOf(DAY)
    : dayjs().tz(AUCKLAND_TIMEZONE).startOf(DAY);

  if (!dueDate || !termStart) return false;

  return dueDate.diff(termStart, DAY) > 90;
};

export const isDueDateTooSoon = (dueDate, minDays = 3) => {
  if (!dueDate) return false;

  const now = dayjs().tz(AUCKLAND_TIMEZONE).startOf("day");
  const due = parseAucklandDate(dueDate)?.startOf("day");

  if (!due) return false;

  const diffDays = due.diff(now, "day");

  return diffDays < minDays;
};

export const hasAnyDueDateTooSoon = (rows = [], minDays = 3) => {
  return rows.some((row) => isDueDateTooSoon(row?.dueDate, minDays));
};

export const hasAnyOver90Days = (rows = []) => {
  return rows.some((row) => isOver90Days(row));
};

// "Future" is judged against today in NZT, not the device's timezone: a
// browser reporting UTC is a calendar day behind NZ for most of the NZ day,
// and would otherwise block an invoice dated today.
export const isFutureDate = (date) => {
  if (!date) return false;

  const today = dayjs().tz(AUCKLAND_TIMEZONE).startOf(DAY);
  const invoiceDay = parseAucklandDate(date)?.startOf(DAY);

  if (!invoiceDay) return false;

  return invoiceDay.isAfter(today);
};
let uniqueIdCounter = 0;
export const generateUniqueId = () => `${Date.now()}-${(uniqueIdCounter += 1)}`;

export const isVoidOrDeleted = (value) => {
  if (!value) return false;
  const upperValue = value.toUpperCase();
  return upperValue.includes("VOID") || upperValue.includes("DELETE");
};

export const truncateText = (value, maxLength) => {
  const trimmed = String(value ?? "").trim();

  return trimmed.length > maxLength
    ? `${trimmed.slice(0, maxLength)}...`
    : trimmed;
};

export const formatAuBranchCode = (value) => {
  const digits = value.replace(/\D/g, "").slice(0, AU_BRANCH_CODE_LENGTH);
  return digits.length > 3
    ? `${digits.slice(0, 3)}-${digits.slice(3)}`
    : digits;
};

// Runs onPoll maxCount times — first after initialDelay, then every interval.
// The pending timeout id is parked on timeoutRef so the caller can clear it.
export const schedulePolling = ({
  timeoutRef,
  onPoll,
  initialDelay,
  interval,
  maxCount
}) => {
  const scheduleNextCall = (delay, callCount) => {
    timeoutRef.current = setTimeout(() => {
      onPoll();

      if (callCount + 1 < maxCount) {
        scheduleNextCall(interval, callCount + 1);
      }
    }, delay);
  };

  scheduleNextCall(initialDelay, 0);
};
